import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, FileText, Info, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  createTemplate, deleteTemplate, hausVorlagenSchreiben, listTemplatesMitInhalt, templateHtml, type Template,
} from '../../lib/supabase';
import { HAUS_VORLAGEN, VORLAGEN_KATEGORIEN, hausHtml, hausVorlage, kategorieText } from '../../lib/mailBuilder/htmlVorlagen';
import type { EmojiStufe } from '../../lib/mailBuilder/vorlagen';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal/Modal';
import { MailVorschau } from '../../components/MailVorschau/MailVorschau';
import { EmojiStufenWahl } from '../../components/EmojiStufenWahl';
import { formatDateTime } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';
import '../Assistent/Assistent.css';
import '../Assistent/MailAssistent.css';
import '../Vorlagen/Vorlagen.css';

/** Alle Haus-Vorlagen in allen drei Emoji-Stufen, so wie sie in die Datenbank kommen. */
function hausZeilen() {
  return HAUS_VORLAGEN.map((v) => ({
    haus: v.key, name: v.name, description: v.beschreibung, category: v.kategorie,
    normal: hausHtml(v, 'normal'), ohne: hausHtml(v, 'ohne'), viele: hausHtml(v, 'viele'),
  }));
}

export function TemplateList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // Vorlagen pflegen ausschließlich Admins. Redakteure verwenden sie im Kampagnen-Editor.
  const isAdmin = user?.role === 'admin';
  const [params, setParams] = useSearchParams();
  const [stufe, setStufe] = useState<EmojiStufe>('normal');
  const [modalOpen, setModalOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', category: 'newsletter' });
  const [meldung, setMeldung] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['templates', 'inhalt'], queryFn: listTemplatesMitInhalt });

  // Haus-Vorlagen in fester Reihenfolge vorn, danach die eigenen (neueste zuerst).
  const { haus, eigene } = useMemo(() => {
    const alle = data ?? [];
    return {
      haus: HAUS_VORLAGEN.map((v) => alle.find((t) => t.haus === v.key)).filter((t): t is Template => !!t),
      eigene: alle.filter((t) => !hausVorlage(t.haus)),
    };
  }, [data]);
  const alle = [...haus, ...eigene];
  const fehlend = HAUS_VORLAGEN.filter((v) => !haus.some((t) => t.haus === v.key));
  const gewaehlt = alle.find((t) => t.id === params.get('vorlage')) ?? alle[0] ?? null;
  const hv = hausVorlage(gewaehlt?.haus);
  const mitStufen = !!gewaehlt?.varianten?.ohne && !!gewaehlt?.varianten?.viele;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['templates'] });

  const createMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: (t) => {
      invalidate();
      setModalOpen(false);
      setNewForm({ name: '', description: '', category: 'newsletter' });
      navigate(`/templates/${t.id}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => { invalidate(); setParams({}, { replace: true }); },
  });

  const hausMutation = useMutation({
    mutationFn: () => hausVorlagenSchreiben(hausZeilen()),
    onSuccess: (r) => {
      invalidate();
      setMeldung(`Haus-Vorlagen sind auf dem neuesten Stand: ${r.aktualisiert} aktualisiert, ${r.neu} neu angelegt.`);
    },
    onError: (e) => setMeldung(`Das hat nicht geklappt: ${(e as Error).message}`),
  });

  const hausAktualisieren = () => {
    if (!confirm(
      'Die Haus-Vorlagen werden auf den aktuellen Stand des Mailtools gesetzt. '
      + 'Wer das HTML einer Haus-Vorlage von Hand geändert hat, verliert diese Änderung. '
      + 'Eigene Vorlagen und fertige Kampagnen bleiben, wie sie sind. Weiter?',
    )) return;
    hausMutation.mutate();
  };

  const loeschen = (t: Template) => {
    if (!confirm(`Vorlage „${t.name}" wirklich löschen? Kampagnen, die mit ihr gebaut wurden, behalten ihren Inhalt.`)) return;
    deleteMutation.mutate(t.id);
  };

  const kachel = (t: Template) => {
    const v = hausVorlage(t.haus);
    const aktiv = t.id === gewaehlt?.id;
    return (
      <button
        key={t.id}
        type="button"
        role="option"
        aria-selected={aktiv}
        className={`auswahl-kachel art-kachel vl-kachel${aktiv ? ' aktiv' : ''}`}
        onClick={() => setParams({ vorlage: t.id }, { replace: true })}
      >
        <span className="as-emoji" aria-hidden="true">{v ? v.emoji : <FileText size={22} />}</span>
        <span className="art-text">
          <strong>{t.name}</strong>
          {t.description && <span className="vl-kurz">{t.description}</span>}
          {t.category && <span className="as-kategorie">{kategorieText(t.category)}</span>}
        </span>
      </button>
    );
  };

  return (
    <div>
      <PageHeader
        title="HTML-Vorlagen"
        subtitle="Vorlagen für den Kampagnen-Editor im Randori-Design. Links wählen, rechts sehen, wie die Mail aussieht."
        actions={isAdmin && (
          <>
            <button className="btn btn-secondary" onClick={hausAktualisieren} disabled={hausMutation.isPending} data-tour="templates-haus">
              <RefreshCw size={16} className={hausMutation.isPending ? 'spin' : undefined} aria-hidden="true" />
              {fehlend.length === HAUS_VORLAGEN.length ? 'Haus-Vorlagen anlegen' : 'Haus-Vorlagen aktualisieren'}
            </button>
            <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} aria-hidden="true" /> Eigene Vorlage</button>
          </>
        )}
      />

      {meldung && (
        <div className={`alert ${hausMutation.isError ? 'alert-danger' : 'alert-success'}`} role="status" style={{ marginBottom: '1rem' }}>
          {hausMutation.isError ? <AlertCircle size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
          <div>{meldung}</div>
        </div>
      )}

      {isAdmin && fehlend.length > 0 && fehlend.length < HAUS_VORLAGEN.length && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <Info size={16} aria-hidden="true" />
          <div>Es fehlen noch {fehlend.map((v) => v.name).join(', ')}. „Haus-Vorlagen aktualisieren" legt sie an.</div>
        </div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : !gewaehlt ? (
        <EmptyState
          icon={FileText}
          title="Noch keine HTML-Vorlagen"
          description="HTML-Vorlagen sind fertige Mails mit Platzhaltern für den Kampagnen-Editor, etwa für Lehrgang, Gürtelprüfung, Event, Camp oder Newsletter."
          action={isAdmin && (
            <button className="btn btn-primary" onClick={hausAktualisieren} disabled={hausMutation.isPending}>
              <RefreshCw size={16} aria-hidden="true" /> Haus-Vorlagen anlegen
            </button>
          )}
        />
      ) : (
        <div className="vl-layout">
          <div className="vl-liste vl-liste--gruppen" data-tour="templates-galerie">
            {haus.length > 0 && (
              <>
                <h2 className="vl-gruppe" id="vl-haus">Haus-Vorlagen</h2>
                <div className="vl-optionen" role="listbox" aria-labelledby="vl-haus">{haus.map(kachel)}</div>
              </>
            )}
            {eigene.length > 0 && (
              <>
                <h2 className="vl-gruppe" id="vl-eigene">Eigene Vorlagen</h2>
                <div className="vl-optionen" role="listbox" aria-labelledby="vl-eigene">{eigene.map(kachel)}</div>
              </>
            )}
          </div>

          <div className="vl-detail">
            <div className="vl-kopf">
              <div>
                <h2>{hv ? `${hv.emoji} ` : ''}{gewaehlt.name}</h2>
                {gewaehlt.description && <p>{gewaehlt.description}</p>}
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/templates/${gewaehlt.id}`)}>
                    <Pencil size={14} aria-hidden="true" /> Bearbeiten
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => loeschen(gewaehlt)} disabled={deleteMutation.isPending}>
                    <Trash2 size={14} aria-hidden="true" /> Löschen
                  </button>
                </div>
              )}
            </div>

            <div className="vl-fakten">
              <p>
                <strong>{hv ? 'Haus-Vorlage' : 'Eigene Vorlage'}</strong>
                {gewaehlt.category && <> · {kategorieText(gewaehlt.category)}</>}
                {' · '}zuletzt geändert {formatDateTime(gewaehlt.updated_at)}
              </p>
              <p style={{ display: 'block' }}>
                <strong>So nutzt Du sie:</strong> Neue Kampagne → Design → Vorlage. Texte und Bilder direkt in der Mail ändern;
                {hv ? ' Platzhalter in [ECKIGEN KLAMMERN] ersetzen — der Versand-Check meldet vergessene.' : ' die Vorlage selbst bleibt dabei unverändert.'}
              </p>
            </div>

            {mitStufen && (
              <div className="vl-stufen" data-tour="templates-emojis">
                <EmojiStufenWahl id="vorlagen-emojis" wert={stufe} onChange={setStufe} />
                <span className="form-hint" style={{ margin: 0 }}>
                  {stufe === 'ohne' ? 'Ganz schlicht, ohne Emojis.' : stufe === 'normal' ? 'Ein Symbol im Kopf, dazu 👋 im Gruß und 🥋 in der Signatur.' : 'Zusätzlich passende Emojis in Überschriften und Eckdaten.'}
                </span>
              </div>
            )}

            <MailVorschau key={`${gewaehlt.id}-${mitStufen ? stufe : 'normal'}`} html={templateHtml(gewaehlt, mitStufen ? stufe : 'normal')} titel="Vorlage mit Platzhaltern" tour="templates-vorschau" />
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Eigene Vorlage">
        <p className="form-hint" style={{ marginTop: 0, marginBottom: '1.25rem' }}>
          Für Mails, die keine Haus-Vorlage abdeckt. Nach dem Anlegen fügst Du das HTML ein.
        </p>
        <div className="form-group">
          <label className="form-label" htmlFor="vorlage-name">Name *</label>
          <input id="vorlage-name" className="form-input" placeholder="z. B. Sommerfest" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="vorlage-beschreibung">Beschreibung</label>
          <textarea id="vorlage-beschreibung" className="form-textarea" rows={2} placeholder="Wofür ist diese Vorlage gedacht?" value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="vorlage-kategorie">Kategorie</label>
          <select id="vorlage-kategorie" className="form-select" value={newForm.category} onChange={(e) => setNewForm({ ...newForm, category: e.target.value })}>
            {VORLAGEN_KATEGORIEN.map((k) => <option key={k.wert} value={k.wert}>{k.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => createMutation.mutate(newForm)} disabled={!newForm.name || createMutation.isPending}>
            {createMutation.isPending ? 'Lege an …' : 'Anlegen & bearbeiten'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
